import React, { useState, useEffect } from 'react';
import { Box, BoxProps } from '@mui/material';

interface HeroVideoProps extends Omit<BoxProps, 'component'> {
  /** Base path (no extension) for the encoded renditions, e.g. "/video/intro-haven". */
  src?: string;
  /** Poster image shown instantly while the video loads / before autoplay starts. */
  poster?: string;
  title?: string;
  width?: number | string;
  height?: number | string;
  /**
   * Autoplay-on-land. The poster paints immediately as the LCP candidate; the
   * autoplaying <video> is mounted once the page has loaded so the ~MBs of
   * video data don't compete with the critical render path and inflate LCP.
   */
  autoplay?: boolean;
}

/**
 * Self-hosted hero video.
 *
 * Replaces the YouTube facade for the homepage hero. A native <video> element
 * has no player application JS (the YouTube iframe ships ~1.4 MB of it) and no
 * third-party connections.
 *
 * To keep LCP fast, the poster is rendered as a plain <img> first — it's the
 * LCP candidate and paints in ~1 frame. The autoplaying <video> (WebM/VP9 with
 * an H.264 MP4 fallback) is mounted on top once the page has loaded, so its
 * multi-MB download never competes with the critical path. The poster is
 * identical to the video's first frame, so the swap is visually seamless and
 * autoplay-on-land is preserved.
 */
const HeroVideo: React.FC<HeroVideoProps> = ({
  src = '/video/intro-haven',
  poster = '/video/intro-haven-poster.webp',
  title = 'Introducing HAVEN: Your Indoor Air Quality Solution',
  width = '100%',
  height = '100%',
  autoplay = true,
  sx,
  ...boxProps
}) => {
  // Mount the <video> only after the page has loaded (or the browser is idle),
  // so its download stays off the critical path and the poster <img> remains
  // the LCP candidate. requestIdleCallback isn't in Safari, hence the fallback.
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const mount = () => {
      if (w.requestIdleCallback) {
        idleId = w.requestIdleCallback(() => setShowVideo(true), { timeout: 2000 });
      } else {
        timeoutId = window.setTimeout(() => setShowVideo(true), 200);
      }
    };

    if (document.readyState === 'complete') {
      mount();
    } else {
      window.addEventListener('load', mount, { once: true });
    }

    return () => {
      window.removeEventListener('load', mount);
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Box
      {...boxProps}
      sx={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Poster — the LCP candidate. Paints instantly; identical to the video's
          first frame so the <video> swap below is seamless. */}
      <Box
        component="img"
        src={poster}
        alt={title}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Autoplaying video, mounted post-load and layered over the poster. */}
      {showVideo && (
        <Box
          component="video"
          poster={poster}
          autoPlay={autoplay}
          muted={autoplay}
          loop
          playsInline
          preload="auto"
          aria-label={title}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        >
          <source src={`${src}.webm`} type="video/webm" />
          <source src={`${src}.mp4`} type="video/mp4" />
        </Box>
      )}
    </Box>
  );
};

export default HeroVideo;
